import React, { useMemo } from "react";
import { Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface CATSentimentProps {
  rows: RawIndicatorRow[];
}

// ─── data parsing ─────────────────────────────────────────────────────────────

interface CATData {
  sectionTitle: string;
  catEngaged: string;
  motion: string;
  role: string;
  score: number;
  maxScore: number;
}

function parseCATData(rows: RawIndicatorRow[]): CATData {
  let catEngaged = "";
  let motion = "";
  let role = "";
  let score = 0;
  let maxScore = 100;
  let sectionTitle = "CAT Sentiment Indicator";

  for (const row of rows) {
    const metric = (row.metricName ?? "").trim();
    const val = String(row.value ?? "").trim();
    if (!sectionTitle && row.sectionName) sectionTitle = row.sectionName;

    if (metric === "CAT Engaged") catEngaged = val;
    else if (metric === "Motion") motion = val;
    else if (metric === "Role") role = val;
    else if (metric === "Score" || metric === "CAT Score") score = Number(val) || 0;
    else if (metric === "Max Score") maxScore = Number(val) || 100;
  }

  // Derive score from engagement if not explicitly provided
  if (score === 0 && catEngaged !== "") {
    score = catEngaged.toLowerCase() === "yes" ? 100 : 0;
  }

  return { sectionTitle, catEngaged, motion, role, score, maxScore };
}

// ─── sub-components ──────────────────────────────────────────────────────────

interface DetailBoxProps {
  label: string;
  value: string;
  valueColor?: string;
  valueBold?: boolean;
}

const DetailBox: React.FC<DetailBoxProps> = ({ label, value, valueColor = "#323130", valueBold = false }) => (
  <Stack
    tokens={{ childrenGap: 4 }}
    horizontalAlign="center"
    verticalAlign="center"
    styles={{
      root: {
        backgroundColor: "#FFFFFF",
        border: "1px solid #EDEBE9",
        borderRadius: 8,
        padding: "10px 16px",
        minWidth: 100,
        flex: "1 1 0",
        textAlign: "center",
      },
    }}
  >
    <Text variant="small" styles={{ root: { color: "#605E5C", fontWeight: 400 } }}>
      {label}
    </Text>
    <Text
      variant="medium"
      styles={{
        root: {
          fontWeight: valueBold ? 700 : 600,
          color: valueColor,
          lineHeight: "20px",
        },
      }}
    >
      {value || "—"}
    </Text>
  </Stack>
);

// ─── main export ──────────────────────────────────────────────────────────────

const IntuneCATSentimentRawIndicatorCards: React.FC<CATSentimentProps> = ({ rows }) => {
  const data = useMemo(() => parseCATData(rows), [rows]);

  const isEngaged = data.catEngaged.toLowerCase() === "yes";
  const scoreColor = data.score >= 71 ? "#107C10" : data.score >= 41 ? "#FFB900" : "#D13438";

  return (
    <Stack
      tokens={{ childrenGap: 16 }}
      styles={{
        root: {
          backgroundColor: "#F3F2F1",
          border: "1px solid #EDEBE9",
          borderRadius: 12,
          padding: "20px 24px",
          maxWidth: 420,
        },
      }}
    >
      {/* Card title */}
      <Text variant="medium" styles={{ root: { color: "#605E5C", fontWeight: 400 } }}>
        {data.sectionTitle}
      </Text>

      {/* Score row */}
      <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 6 }}>
        <Text
          styles={{
            root: {
              fontSize: 42,
              fontWeight: 700,
              color: scoreColor,
              lineHeight: "48px",
            },
          }}
        >
          {data.score}
        </Text>
        <Text variant="large" styles={{ root: { color: "#A19F9D", fontWeight: 400 } }}>
          / {data.maxScore}
        </Text>
      </Stack>

      {/* Status badge */}
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Text variant="medium" styles={{ root: { color: "#323130" } }}>Status:</Text>
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          styles={{
            root: {
              backgroundColor: isEngaged ? "#107C10" : "#D13438",
              borderRadius: 4,
              padding: "2px 12px",
            },
          }}
        >
          <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 600 } }}>
            {isEngaged ? "Engaged" : "Not Engaged"}
          </Text>
        </Stack>
      </Stack>

      {/* Divider */}
      <div style={{ height: 1, backgroundColor: "#EDEBE9" }} />

      {/* Detail boxes */}
      <Stack tokens={{ childrenGap: 6 }}>
        <Text variant="small" styles={{ root: { color: "#A19F9D" } }}>
          Double-click for details:
        </Text>
        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { flexWrap: "wrap" } }}>
          <DetailBox
            label="CAT Engaged"
            value={data.catEngaged}
            valueColor={isEngaged ? "#107C10" : "#D13438"}
            valueBold
          />
          {data.motion && (
            <DetailBox label="Motion" value={data.motion} />
          )}
          {data.role && (
            <DetailBox label="Role" value={data.role} />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default IntuneCATSentimentRawIndicatorCards;
